/**
 * AdvertiserMacrosPage — macro template management for advertiser postbacks.
 *
 * Three panels:
 *   1. Advertiser scope input + filter + list of existing templates.
 *   2. Add / edit form (name + newline-delimited key=value macro blob).
 *   3. Live substitution preview: user fills a context blob; the page renders
 *      the selected template's macros against it using the local `previewRender`
 *      helper, so the substitution is visible without a round trip.
 *
 * Palette: neutral zinc shells + green-700 headline + green-600 primary buttons.
 * No emerald / blue / orange / purple / dark / emoji / competitor references.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	filterByName,
	parseContextBlob,
	parseMacrosBlob,
	previewRender,
	serializeMacrosBlob,
	type AdvertiserMacroRow,
	type MacroMap,
} from "./api";
import {
	useAdvertiserMacrosCreate,
	useAdvertiserMacrosDelete,
	useAdvertiserMacrosList,
	useAdvertiserMacrosUpdate,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const TEXTAREA =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 font-mono text-xs text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_ACCENT =
	"inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800";

interface FormState {
	mode: "create" | "edit";
	editingId: string | null;
	name: string;
	macrosBlob: string;
}

const EMPTY_FORM: FormState = {
	mode: "create",
	editingId: null,
	name: "",
	macrosBlob: "",
};

export function AdvertiserMacrosPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [filter, setFilter] = useState("");
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [contextBlob, setContextBlob] = useState(
		"click_id=abc123\npayout=12.50\ncountry=US",
	);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const listQuery = useAdvertiserMacrosList(advertiserId.trim());
	const createMut = useAdvertiserMacrosCreate();
	const updateMut = useAdvertiserMacrosUpdate();
	const deleteMut = useAdvertiserMacrosDelete();

	const rows = useMemo<AdvertiserMacroRow[]>(
		() => listQuery.data ?? [],
		[listQuery.data],
	);
	const filtered = useMemo(
		() => filterByName(rows, filter),
		[rows, filter],
	);

	const selected = useMemo(
		() => rows.find((r) => r.id === selectedId) ?? null,
		[rows, selectedId],
	);

	const context = useMemo(
		() => parseContextBlob(contextBlob),
		[contextBlob],
	);

	const previewResolved = useMemo<MacroMap>(() => {
		if (!selected) return {};
		return previewRender(selected.macros, context);
	}, [selected, context]);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const name = form.name.trim();
		const advId = advertiserId.trim();
		if (!name || !advId) return;
		const macros = parseMacrosBlob(form.macrosBlob);

		if (form.mode === "edit" && form.editingId) {
			updateMut.mutate(
				{ id: form.editingId, body: { name, macros } },
				{ onSuccess: () => setForm(EMPTY_FORM) },
			);
		} else {
			createMut.mutate(
				{ advertiser_id: advId, name, macros },
				{ onSuccess: () => setForm(EMPTY_FORM) },
			);
		}
	};

	const startEdit = (row: AdvertiserMacroRow) => {
		setForm({
			mode: "edit",
			editingId: row.id,
			name: row.name,
			macrosBlob: serializeMacrosBlob(row.macros),
		});
		setSelectedId(row.id);
	};

	const cancelEdit = () => setForm(EMPTY_FORM);

	const mutating =
		createMut.isPending || updateMut.isPending || deleteMut.isPending;
	const mutError =
		createMut.error?.message ||
		updateMut.error?.message ||
		deleteMut.error?.message ||
		null;

	return (
		<main
			data-testid="advertiser-macros-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-macros
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Advertiser macro templates
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Define named macro maps per advertiser. Each value supports{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5 text-xs text-zinc-800">
						{"{{token}}"}
					</code>{" "}
					substitution at postback fire time. Use the preview panel to
					verify substitution results against a sample context.
				</p>
			</header>

			<section className={CARD} aria-label="Advertiser scope">
				<label className="flex flex-col gap-2 text-sm text-zinc-700">
					<span className="font-medium text-zinc-900">Advertiser ID</span>
					<input
						type="text"
						className={INPUT}
						placeholder="e.g. adv_01H..."
						value={advertiserId}
						onChange={(e) => {
							setAdvertiserId(e.currentTarget.value);
							setSelectedId(null);
						}}
						data-testid="advertiser-macros-advertiser-input"
					/>
					<span className="text-xs text-zinc-500">
						Templates are listed and created under this advertiser scope.
					</span>
				</label>
			</section>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<section
					className={CARD}
					aria-label="Macro template list"
					data-testid="advertiser-macros-list-panel"
				>
					<div className="flex items-center justify-between gap-3">
						<h2 className="text-base font-semibold text-zinc-900">
							Templates
						</h2>
						<span className={PILL_ACCENT} data-testid="advertiser-macros-count">
							{filtered.length} shown
						</span>
					</div>
					<input
						type="text"
						className={`${INPUT} mt-3`}
						placeholder="Filter by name or id."
						value={filter}
						onChange={(e) => setFilter(e.currentTarget.value)}
						data-testid="advertiser-macros-filter-input"
					/>

					{!advertiserId.trim() ? (
						<p
							className="mt-4 text-sm text-zinc-500"
							data-testid="advertiser-macros-need-advertiser"
						>
							Enter an advertiser id to load templates.
						</p>
					) : listQuery.isLoading ? (
						<p
							className="mt-4 text-sm text-zinc-500"
							data-testid="advertiser-macros-loading"
						>
							Loading.
						</p>
					) : listQuery.error ? (
						<p
							className="mt-4 text-sm text-red-700"
							role="alert"
							data-testid="advertiser-macros-list-error"
						>
							{listQuery.error.message}
						</p>
					) : filtered.length === 0 ? (
						<p
							className="mt-4 text-sm text-zinc-500"
							data-testid="advertiser-macros-empty"
						>
							{rows.length === 0
								? "No templates yet. Create one on the right."
								: "No templates match that filter."}
						</p>
					) : (
						<ul
							className="mt-3 divide-y divide-zinc-200"
							data-testid="advertiser-macros-list"
						>
							{filtered.map((row) => {
								const keyCount = Object.keys(row.macros).length;
								const active = selectedId === row.id;
								return (
									<li
										key={row.id}
										className={`flex items-center justify-between gap-3 py-2 ${
											active ? "bg-green-50/40" : ""
										}`}
										data-testid={`advertiser-macros-row-${row.id}`}
									>
										<button
											type="button"
											className="flex min-w-0 flex-1 flex-col items-start text-left"
											onClick={() => setSelectedId(row.id)}
											data-testid={`advertiser-macros-select-${row.id}`}
										>
											<span className="text-sm font-medium text-zinc-900">
												{row.name}
											</span>
											<span className="truncate font-mono text-[11px] text-zinc-500">
												{row.id}
											</span>
											<span className="mt-1 text-xs text-zinc-600">
												{keyCount} macro{keyCount === 1 ? "" : "s"}
											</span>
										</button>
										<div className="flex shrink-0 items-center gap-2">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => startEdit(row)}
												disabled={mutating}
												data-testid={`advertiser-macros-edit-${row.id}`}
											>
												Edit
											</button>
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => {
													deleteMut.mutate(row.id, {
														onSuccess: () => {
															if (selectedId === row.id) setSelectedId(null);
															if (form.editingId === row.id) cancelEdit();
														},
													});
												}}
												disabled={mutating}
												data-testid={`advertiser-macros-delete-${row.id}`}
											>
												Delete
											</button>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</section>

				<section
					className={CARD}
					aria-label="Template form"
					data-testid="advertiser-macros-form-panel"
				>
					<div className="flex items-center justify-between gap-3">
						<h2 className="text-base font-semibold text-zinc-900">
							{form.mode === "edit" ? "Edit template" : "New template"}
						</h2>
						{form.mode === "edit" ? (
							<button
								type="button"
								className={BTN_GHOST}
								onClick={cancelEdit}
								disabled={mutating}
								data-testid="advertiser-macros-cancel-edit"
							>
								Cancel edit
							</button>
						) : null}
					</div>
					<form
						onSubmit={handleSubmit}
						className="mt-3 flex flex-col gap-3"
						data-testid="advertiser-macros-create-form"
					>
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="font-medium text-zinc-900">Name</span>
							<input
								type="text"
								className={INPUT}
								placeholder="e.g. default, signup, premium"
								value={form.name}
								onChange={(e) =>
									setForm((f) => ({ ...f, name: e.currentTarget.value }))
								}
								disabled={mutating}
								data-testid="advertiser-macros-name-input"
							/>
						</label>
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="font-medium text-zinc-900">
								Macros ({"key=template"} per line)
							</span>
							<textarea
								className={TEXTAREA}
								rows={7}
								placeholder={
									"click_id={{click_id}}\npayout={{payout}}\ncurrency=USD"
								}
								value={form.macrosBlob}
								onChange={(e) =>
									setForm((f) => ({
										...f,
										macrosBlob: e.currentTarget.value,
									}))
								}
								disabled={mutating}
								data-testid="advertiser-macros-macros-input"
							/>
							<span className="text-xs text-zinc-500">
								Use {"{{token}}"} to reference a context key. Unknown tokens
								stay literal.
							</span>
						</label>
						<div className="flex items-center gap-2">
							<button
								type="submit"
								className={BTN_PRIMARY}
								disabled={
									mutating ||
									!form.name.trim() ||
									!advertiserId.trim()
								}
								data-testid="advertiser-macros-submit"
							>
								{mutating
									? "Saving."
									: form.mode === "edit"
										? "Save changes"
										: "Create template"}
							</button>
							{mutError ? (
								<span
									className="text-xs text-red-700"
									role="alert"
									data-testid="advertiser-macros-form-error"
								>
									{mutError}
								</span>
							) : null}
						</div>
					</form>
				</section>
			</div>

			<section
				className={CARD}
				aria-label="Substitution preview"
				data-testid="advertiser-macros-preview-panel"
			>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Substitution preview
					</h2>
					{selected ? (
						<span className="text-xs text-zinc-500">
							Previewing{" "}
							<span className="font-medium text-zinc-800">{selected.name}</span>
						</span>
					) : (
						<span className="text-xs text-zinc-500">
							Select a template to preview.
						</span>
					)}
				</div>

				<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
					<label className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="font-medium text-zinc-900">
							Context ({"key=value"} per line)
						</span>
						<textarea
							className={TEXTAREA}
							rows={8}
							value={contextBlob}
							onChange={(e) => setContextBlob(e.currentTarget.value)}
							data-testid="advertiser-macros-context-input"
						/>
					</label>
					<div className="flex flex-col gap-1 text-sm text-zinc-700">
						<span className="font-medium text-zinc-900">Resolved output</span>
						{selected ? (
							Object.keys(previewResolved).length === 0 ? (
								<p
									className="text-sm text-zinc-500"
									data-testid="advertiser-macros-preview-empty"
								>
									This template has no macros defined.
								</p>
							) : (
								<ul
									className="divide-y divide-zinc-200 rounded-[6px] border border-zinc-200 bg-zinc-50"
									data-testid="advertiser-macros-preview-list"
								>
									{Object.entries(previewResolved).map(([k, v]) => (
										<li
											key={k}
											className="flex items-start gap-3 px-3 py-2 font-mono text-xs"
											data-testid={`advertiser-macros-preview-row-${k}`}
										>
											<span className="shrink-0 text-zinc-500">{k}</span>
											<span className="text-zinc-400">=</span>
											<span
												className="min-w-0 flex-1 break-all text-green-800"
												data-testid={`advertiser-macros-preview-value-${k}`}
											>
												{v}
											</span>
										</li>
									))}
								</ul>
							)
						) : (
							<p
								className="text-sm text-zinc-500"
								data-testid="advertiser-macros-preview-placeholder"
							>
								Pick a template from the list on the left.
							</p>
						)}
					</div>
				</div>
			</section>
		</main>
	);
}

export default AdvertiserMacrosPage;
